/* Lost Culture Studio — theme behavior (vanilla JS, no dependencies) */
(() => {
  'use strict';

  const theme = window.theme || { routes: {}, settings: {}, strings: {} };
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Utilities ---------- */
  const storage = {
    get(key, fallback) {
      try {
        const raw = window.localStorage.getItem(key);
        return raw === null ? fallback : JSON.parse(raw);
      } catch (e) {
        return fallback;
      }
    },
    set(key, value) {
      try { window.localStorage.setItem(key, JSON.stringify(value)); } catch (e) { /* storage unavailable */ }
    }
  };

  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const fmt = format || theme.settings.moneyFormat || '${{amount}}';
    const value = Number(cents) || 0;
    const delimit = (n, precision, thousands, decimal) => {
      const fixed = (n / 100).toFixed(precision);
      const parts = fixed.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
      return parts[1] ? dollars + decimal + parts[1] : dollars;
    };
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => {
      switch (key) {
        case 'amount': return delimit(value, 2, ',', '.');
        case 'amount_no_decimals': return delimit(value, 0, ',', '.');
        case 'amount_with_comma_separator': return delimit(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return delimit(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return delimit(value, 2, "'", '.');
        case 'amount_with_space_separator': return delimit(value, 2, ' ', ',');
        case 'amount_no_decimals_with_space_separator': return delimit(value, 0, ' ', ',');
        default: return delimit(value, 2, ',', '.');
      }
    });
  }

  function debounce(fn, wait) {
    let t;
    return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), wait); };
  }

  function parseHTML(html) {
    return new DOMParser().parseFromString(html, 'text/html');
  }

  let toastTimer;
  function toast(message) {
    const el = $('[data-toast]');
    if (!el) return;
    el.textContent = message;
    el.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => el.classList.remove('is-visible'), 3200);
  }

  /* ---------- Drawers ---------- */
  const Drawers = {
    current: null,
    trigger: null,
    overlay: null,

    init() {
      this.overlay = $('[data-overlay]');
      document.addEventListener('click', (e) => {
        const opener = e.target.closest('[data-open-drawer]');
        if (opener) {
          const name = opener.getAttribute('data-open-drawer');
          if (name === 'cart' && theme.settings.cartType === 'page') return;
          if (!$(`[data-drawer="${name}"]`)) return;
          e.preventDefault();
          this.open(name, opener);
          return;
        }
        if (e.target.closest('[data-close-drawer]')) {
          e.preventDefault();
          this.close();
          return;
        }
        if (this.current && e.target.classList && e.target.classList.contains('drawer')) this.close();
      });
      if (this.overlay) this.overlay.addEventListener('click', () => this.close());
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.current) this.close();
        if (e.key === 'Tab' && this.current) this.trapFocus(e);
      });
    },

    open(name, trigger) {
      const drawer = $(`[data-drawer="${name}"]`);
      if (!drawer) return;
      if (this.current && this.current !== drawer) this.close(true);
      Mega.close();
      this.current = drawer;
      this.trigger = trigger || document.activeElement;
      drawer.hidden = false;
      if (this.overlay) this.overlay.hidden = false;
      document.body.classList.add('is-locked');
      $$(`[data-open-drawer="${name}"]`).forEach((b) => b.setAttribute('aria-expanded', 'true'));
      requestAnimationFrame(() => {
        drawer.classList.add('is-open');
        if (this.overlay) this.overlay.classList.add('is-visible');
        const focusTarget = $('[data-predictive-input]', drawer) || $('input[type="email"]', drawer) || $('[data-close-drawer]', drawer);
        if (focusTarget) setTimeout(() => focusTarget.focus({ preventScroll: true }), reduceMotion ? 0 : 120);
      });
      document.dispatchEvent(new CustomEvent('drawer:open', { detail: { name } }));
    },

    close(immediate) {
      const drawer = this.current;
      if (!drawer) return;
      const name = drawer.getAttribute('data-drawer');
      drawer.classList.remove('is-open');
      $$(`[data-open-drawer="${name}"]`).forEach((b) => b.setAttribute('aria-expanded', 'false'));
      const finish = () => {
        drawer.hidden = true;
        if (!this.current) {
          if (this.overlay) this.overlay.hidden = true;
          document.body.classList.remove('is-locked');
        }
      };
      this.current = null;
      if (this.overlay && !immediate) this.overlay.classList.remove('is-visible');
      if (immediate || reduceMotion) finish(); else setTimeout(finish, 450);
      if (!immediate && this.trigger && document.contains(this.trigger)) this.trigger.focus({ preventScroll: true });
      document.dispatchEvent(new CustomEvent('drawer:close', { detail: { name } }));
    },

    trapFocus(e) {
      const focusables = $$('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])', this.current)
        .filter((el) => el.offsetParent !== null || el === document.activeElement);
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  };

  /* ---------- Header & mega menu ---------- */
  const Mega = {
    toggle: null, panel: null, header: null,
    init() {
      this.header = $('[data-header]');
      this.toggle = $('[data-mega-toggle]');
      this.panel = $('[data-mega]');
      if (!this.toggle || !this.panel) return;
      this.toggle.addEventListener('click', () => (this.isOpen() ? this.close() : this.open()));
      document.addEventListener('click', (e) => {
        if (this.isOpen() && !e.target.closest('[data-mega]') && !e.target.closest('[data-mega-toggle]')) this.close();
      });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen()) { this.close(); this.toggle.focus(); }
      });
      this.panel.addEventListener('focusout', (e) => {
        if (this.isOpen() && e.relatedTarget && !this.panel.contains(e.relatedTarget) && e.relatedTarget !== this.toggle) this.close();
      });
    },
    isOpen() { return this.toggle && this.toggle.getAttribute('aria-expanded') === 'true'; },
    open() {
      this.panel.hidden = false;
      this.toggle.setAttribute('aria-expanded', 'true');
      this.header.classList.add('is-mega-open');
      Header.update();
      requestAnimationFrame(() => this.panel.classList.add('is-open'));
    },
    close() {
      if (!this.isOpen()) return;
      this.toggle.setAttribute('aria-expanded', 'false');
      this.panel.classList.remove('is-open');
      this.header.classList.remove('is-mega-open');
      Header.update();
      setTimeout(() => { if (!this.isOpen()) this.panel.hidden = true; }, reduceMotion ? 0 : 450);
    }
  };

  const Header = {
    el: null,
    init() {
      this.el = $('[data-header]');
      if (!this.el || !this.el.hasAttribute('data-transparent')) return;
      const firstSection = $('.main > .shopify-section');
      const hasHero = firstSection && firstSection.querySelector('[data-hero]');
      if (!hasHero) {
        this.el.classList.remove('is-over-hero', 'site-header--transparent');
        this.el.removeAttribute('data-transparent');
        return;
      }
      document.body.classList.add('has-transparent-header');
      this.update();
      window.addEventListener('scroll', () => this.update(), { passive: true });
    },
    update() {
      if (!this.el || !this.el.hasAttribute('data-transparent')) return;
      const solid = window.scrollY > 24 || this.el.classList.contains('is-mega-open');
      this.el.classList.toggle('is-over-hero', !solid);
    }
  };

  /* ---------- Cart ---------- */
  const Cart = {
    sectionId: 'cart-drawer',

    init() {
      document.addEventListener('click', (e) => {
        const qtyBtn = e.target.closest('[data-cart-qty]');
        if (qtyBtn) {
          e.preventDefault();
          const line = qtyBtn.closest('[data-cart-line]');
          this.change(line, Math.max(0, parseInt(qtyBtn.getAttribute('data-cart-qty'), 10) || 0));
          return;
        }
        const remove = e.target.closest('[data-cart-remove]');
        if (remove) {
          e.preventDefault();
          this.change(remove.closest('[data-cart-line]'), 0);
        }
      });
      document.addEventListener('change', (e) => {
        const input = e.target.closest('[data-cart-qty-input]');
        if (input) this.change(input.closest('[data-cart-line]'), Math.max(0, parseInt(input.value, 10) || 0));
      });
    },

    async add(form) {
      const formData = new FormData(form);
      const body = {
        items: [{ id: Number(formData.get('id')), quantity: Number(formData.get('quantity') || 1) }],
        sections: this.sectionId,
        sections_url: window.location.pathname
      };
      const res = await fetch(`${theme.routes.cartAdd}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (!res.ok || data.status) {
        throw new Error(data.description || data.message || theme.strings.cartError);
      }
      if (data.sections) this.render(data.sections[this.sectionId]);
      await this.refreshCount();
      return data;
    },

    async change(line, quantity) {
      if (!line) return;
      const key = line.getAttribute('data-key');
      const errorEl = line.closest('[data-cart-drawer], [data-cart-page]');
      const errorBox = errorEl ? $('[data-cart-error]', errorEl) : null;
      line.classList.add('is-updating');
      try {
        const res = await fetch(`${theme.routes.cartChange}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity, sections: this.sectionId, sections_url: window.location.pathname })
        });
        const data = await res.json();
        if (!res.ok || data.status) throw new Error(data.description || data.message || theme.strings.cartError);
        if (line.closest('[data-cart-page]')) { window.location.reload(); return; }
        if (data.sections) this.render(data.sections[this.sectionId]);
        this.setCount(data.item_count);
      } catch (err) {
        line.classList.remove('is-updating');
        if (errorBox) { errorBox.textContent = err.message || theme.strings.cartError; errorBox.hidden = false; }
        else toast(err.message || theme.strings.cartError);
      }
    },

    render(html) {
      if (!html) return;
      const doc = parseHTML(html);
      const fresh = $('[data-cart-drawer]', doc);
      const current = $('[data-cart-drawer]');
      if (fresh && current) current.innerHTML = fresh.innerHTML;
    },

    async refreshCount() {
      try {
        const res = await fetch(`${theme.routes.cart}.js`, { headers: { Accept: 'application/json' } });
        const cart = await res.json();
        this.setCount(cart.item_count);
      } catch (e) { /* count stays as rendered */ }
    },

    setCount(count) {
      $$('[data-cart-count]').forEach((el) => { el.textContent = count; });
    }
  };

  /* ---------- Product form & variants ---------- */
  class ProductForm {
    constructor(wrapper) {
      this.wrapper = wrapper;
      this.form = $('[data-product-form]', wrapper);
      const json = $('[data-product-json]', wrapper);
      this.data = json ? JSON.parse(json.textContent) : null;
      this.section = wrapper.closest('[data-product-section]') || document;
      this.isMain = !!wrapper.closest('.section-main-product');
      this.idInput = $('[data-variant-id]', wrapper);
      this.addBtn = $('[data-add-button]', wrapper);
      this.addLabel = $('[data-add-label]', wrapper);
      this.errorEl = $('[data-product-error]', wrapper);
      this.bind();
      this.updateAvailability();
    }

    bind() {
      this.wrapper.addEventListener('change', (e) => {
        if (e.target.matches('[data-option-input]')) this.onOptionChange();
      });
      $$('[data-qty-step]', this.wrapper).forEach((btn) => {
        btn.addEventListener('click', () => {
          const input = $('[data-qty-input]', this.wrapper);
          const next = Math.max(1, (parseInt(input.value, 10) || 1) + Number(btn.getAttribute('data-qty-step')));
          input.value = next;
        });
      });
      if (this.form) this.form.addEventListener('submit', (e) => this.onSubmit(e));
    }

    selectedOptions() {
      return $$('[data-option-index]', this.wrapper).map((group) => {
        const checked = $('[data-option-input]:checked', group);
        return checked ? checked.value : null;
      });
    }

    findVariant(options) {
      if (!this.data) return null;
      return this.data.variants.find((v) => v.options.every((val, i) => val === options[i])) || null;
    }

    onOptionChange() {
      const options = this.selectedOptions();
      $$('[data-option-index]', this.wrapper).forEach((group, i) => {
        const label = $('[data-option-value]', group);
        if (label) label.textContent = options[i] || '';
      });
      const variant = this.findVariant(options);
      this.updateAvailability();
      this.applyVariant(variant);
    }

    updateAvailability() {
      if (!this.data) return;
      const selected = this.selectedOptions();
      $$('[data-option-index]', this.wrapper).forEach((group, i) => {
        $$('[data-option-input]', group).forEach((input) => {
          const candidate = selected.slice();
          candidate[i] = input.value;
          const match = this.data.variants.find((v) => v.options.every((val, idx) => val === candidate[idx]));
          input.classList.toggle('is-unavailable', !match || !match.available);
        });
      });
    }

    applyVariant(variant) {
      if (this.errorEl) this.errorEl.hidden = true;
      if (!variant) {
        this.setButton(false, theme.strings.unavailable);
        return;
      }
      this.idInput.value = variant.id;
      this.setButton(variant.available, variant.available ? theme.strings.addToCart : theme.strings.soldOut);
      const priceBox = $('[data-product-price]', this.section);
      if (priceBox) {
        const sale = variant.compare_at_price;
        priceBox.innerHTML = `<div class="price${sale ? ' price--sale' : ''}" data-price>
          <span class="price__current">${variant.price}</span>
          ${sale ? `<s class="price__compare">${sale}</s>` : ''}
        </div>`;
      }
      if (this.isMain) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
        if (variant.media_id) Gallery.goTo(variant.media_id);
      }
    }

    setButton(enabled, label) {
      if (!this.addBtn) return;
      this.addBtn.disabled = !enabled;
      if (this.addLabel) this.addLabel.textContent = label;
    }

    async onSubmit(e) {
      if (theme.settings.cartType === 'page') return; // native post to /cart/add
      e.preventDefault();
      if (!this.addBtn || this.addBtn.disabled) return;
      this.addBtn.classList.add('is-loading');
      this.addBtn.setAttribute('aria-busy', 'true');
      if (this.addLabel) this.addLabel.textContent = theme.strings.adding;
      try {
        await Cart.add(this.form);
        if (this.addLabel) this.addLabel.textContent = theme.strings.addToCart;
        Drawers.open('cart', this.addBtn);
      } catch (err) {
        if (this.addLabel) this.addLabel.textContent = theme.strings.addToCart;
        if (this.errorEl) { this.errorEl.textContent = err.message; this.errorEl.hidden = false; }
      } finally {
        this.addBtn.classList.remove('is-loading');
        this.addBtn.removeAttribute('aria-busy');
      }
    }
  }

  function initProductForms(root = document) {
    $$('[data-product-form-wrapper]', root).forEach((w) => {
      if (w.dataset.ready) return;
      w.dataset.ready = 'true';
      new ProductForm(w);
    });
  }

  /* ---------- Product gallery (mobile swipe counter, variant jump) ---------- */
  const Gallery = {
    track: null,
    init() {
      this.track = $('[data-gallery-track]');
      const indexEl = $('[data-gallery-index]');
      if (!this.track || !indexEl) return;
      this.track.addEventListener('scroll', debounce(() => {
        const i = Math.round(this.track.scrollLeft / this.track.clientWidth);
        indexEl.textContent = i + 1;
      }, 60), { passive: true });
    },
    goTo(mediaId) {
      if (!this.track) return;
      const item = $(`[data-media-id="${mediaId}"]`, this.track);
      if (!item) return;
      if (window.matchMedia('(max-width: 989px)').matches) {
        this.track.scrollTo({ left: item.offsetLeft, behavior: reduceMotion ? 'auto' : 'smooth' });
      } else if (item !== this.track.firstElementChild) {
        this.track.prepend(item);
      }
    }
  };

  /* ---------- Quick view ---------- */
  const QuickView = {
    init() {
      const content = $('[data-quick-view-content]');
      if (!content) return;
      document.addEventListener('click', async (e) => {
        const btn = e.target.closest('[data-quick-view]');
        if (!btn) return;
        e.preventDefault();
        content.innerHTML = '';
        content.classList.add('is-loading');
        Drawers.open('quick-view', btn);
        try {
          const res = await fetch(`${btn.getAttribute('data-quick-view')}?section_id=quick-view`);
          if (!res.ok) throw new Error();
          const doc = parseHTML(await res.text());
          const qv = $('.qv', doc);
          if (!qv) throw new Error();
          content.innerHTML = '';
          content.appendChild(qv);
          initProductForms(content);
        } catch (err) {
          window.location.href = btn.getAttribute('data-quick-view');
        } finally {
          content.classList.remove('is-loading');
        }
      });
    }
  };

  /* ---------- Predictive search ---------- */
  const Search = {
    input: null, results: null, controller: null, initial: '',
    init() {
      this.input = $('[data-predictive-input]');
      this.results = $('[data-predictive-results]');
      if (!this.input || !this.results) return;
      this.initial = this.results.innerHTML;
      if (!theme.settings.predictiveSearch) return;
      this.input.addEventListener('input', debounce(() => this.query(this.input.value.trim()), 220));
      this.input.addEventListener('keydown', (e) => this.onKey(e));
    },
    async query(q) {
      if (this.controller) this.controller.abort();
      if (!q) { this.results.innerHTML = this.initial; this.input.setAttribute('aria-expanded', 'false'); return; }
      this.controller = new AbortController();
      const panel = this.results.closest('.search-panel');
      if (panel) panel.classList.add('is-loading');
      const params = new URLSearchParams({
        q,
        section_id: 'predictive-search',
        'resources[type]': 'product,collection,query',
        'resources[limit]': '6',
        'resources[options][unavailable_products]': 'last'
      });
      try {
        const res = await fetch(`${theme.routes.predictiveSearch}?${params.toString()}`, { signal: this.controller.signal });
        if (!res.ok) throw new Error(String(res.status));
        const doc = parseHTML(await res.text());
        const node = $('.predictive', doc);
        this.results.innerHTML = node ? node.outerHTML : `<p class="search-panel__hint">${theme.strings.searchNoResults}</p>`;
        this.input.setAttribute('aria-expanded', 'true');
      } catch (err) {
        if (err.name !== 'AbortError') {
          this.results.innerHTML = `<p class="search-panel__hint">${theme.strings.searchNoResults}</p>`;
        }
      } finally {
        if (panel) panel.classList.remove('is-loading');
      }
    },
    options() {
      return $$('[role="option"], [data-predictive-all]', this.results);
    },
    onKey(e) {
      const opts = this.options();
      if (!opts.length || !['ArrowDown', 'ArrowUp', 'Enter'].includes(e.key)) return;
      let idx = opts.findIndex((o) => o.getAttribute('aria-selected') === 'true');
      if (e.key === 'Enter') {
        if (idx > -1) {
          e.preventDefault();
          const link = opts[idx].matches('a') ? opts[idx] : $('a', opts[idx]);
          if (link) window.location.href = link.href;
        }
        return;
      }
      e.preventDefault();
      if (idx > -1) opts[idx].setAttribute('aria-selected', 'false');
      idx = e.key === 'ArrowDown' ? (idx + 1) % opts.length : (idx - 1 + opts.length) % opts.length;
      opts[idx].setAttribute('aria-selected', 'true');
      if (opts[idx].id) this.input.setAttribute('aria-activedescendant', opts[idx].id);
      opts[idx].scrollIntoView({ block: 'nearest' });
    }
  };

  /* ---------- Wishlist (device storage) ---------- */
  const Wishlist = {
    key: 'lcs:wishlist',
    init() {
      if (!theme.settings.wishlist) return;
      this.sync();
      document.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-wishlist-toggle]');
        if (btn) {
          e.preventDefault();
          e.stopPropagation();
          this.toggle(btn.getAttribute('data-handle'), btn);
          return;
        }
        const remove = e.target.closest('[data-wl-remove]');
        if (remove) {
          const item = remove.closest('[data-wishlist-item]');
          this.remove(item.getAttribute('data-handle'));
          item.remove();
          this.renderEmptyState();
        }
      });
      document.addEventListener('drawer:open', (e) => { if (e.detail.name === 'wishlist') this.render(); });
      window.addEventListener('storage', (e) => { if (e.key === this.key) this.sync(); });
    },
    list() { const l = storage.get(this.key, []); return Array.isArray(l) ? l : []; },
    save(list) { storage.set(this.key, list); this.sync(); },
    has(handle) { return this.list().includes(handle); },
    toggle(handle, btn) {
      const list = this.list();
      const i = list.indexOf(handle);
      if (i > -1) list.splice(i, 1); else list.unshift(handle);
      this.save(list);
      if (btn && i === -1) {
        btn.classList.remove('is-pop'); void btn.offsetWidth; btn.classList.add('is-pop');
      }
    },
    remove(handle) { this.save(this.list().filter((h) => h !== handle)); },
    sync() {
      const list = this.list();
      $$('[data-wishlist-count]').forEach((el) => { el.textContent = list.length; });
      $$('[data-wishlist-toggle]').forEach((btn) => {
        const on = list.includes(btn.getAttribute('data-handle'));
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
        const label = $('[data-wishlist-label]', btn);
        if (label) label.textContent = on ? theme.strings.wishlistRemove : theme.strings.wishlistAdd;
      });
    },
    renderEmptyState() {
      const empty = $('[data-wishlist-empty]');
      if (empty) empty.hidden = this.list().length > 0;
    },
    async render() {
      const listEl = $('[data-wishlist-items]');
      const tpl = $('#WishlistItemTemplate');
      if (!listEl || !tpl) return;
      const handles = this.list();
      this.renderEmptyState();
      listEl.innerHTML = '';
      const results = await Promise.all(handles.map(async (handle) => {
        try {
          const res = await fetch(`${theme.routes.root.replace(/\/$/, '')}/products/${encodeURIComponent(handle)}.js`, { headers: { Accept: 'application/json' } });
          if (!res.ok) return { handle, missing: true };
          return { handle, product: await res.json() };
        } catch (e) {
          return { handle, missing: true };
        }
      }));
      results.forEach(({ handle, product, missing }) => {
        const node = tpl.content.firstElementChild.cloneNode(true);
        node.setAttribute('data-handle', handle);
        const img = $('[data-wl-img]', node);
        if (missing) {
          $('[data-wl-title]', node).textContent = handle.replace(/-/g, ' ');
          $('[data-wl-price]', node).textContent = theme.strings.wishlistUnavailable;
          $$('[data-wl-link]', node).forEach((a) => a.removeAttribute('href'));
          img.remove();
        } else {
          $$('[data-wl-link]', node).forEach((a) => { a.href = product.url; });
          $('[data-wl-title]', node).textContent = product.title;
          const from = product.price_varies ? `${theme.strings.from} ` : '';
          $('[data-wl-price]', node).textContent = from + formatMoney(product.price_min);
          if (product.featured_image) {
            const src = product.featured_image.startsWith('//') ? `https:${product.featured_image}` : product.featured_image;
            img.src = `${src}${src.includes('?') ? '&' : '?'}width=300`;
            img.alt = product.title;
          } else {
            img.remove();
          }
        }
        listEl.appendChild(node);
      });
    }
  };

  /* ---------- Membership & newsletter ---------- */
  const Membership = {
    key: 'lcs:membership',
    init() {
      const state = storage.get(this.key, {});
      const params = new URLSearchParams(window.location.search);
      const posted = params.get('customer_posted') === 'true';
      if (posted) {
        storage.set(this.key, { joined: true });
        if (window.location.hash === '#MembershipForm') {
          setTimeout(() => Drawers.open('membership'), 300);
        } else {
          const success = $(`${window.location.hash || '#__none'} [data-signup-success]`);
          if (success) success.focus({ preventScroll: false });
        }
      }
      // Re-open the drawer if the membership form returned a validation error
      if (!posted && window.location.hash === '#MembershipForm' && $('#MembershipForm .signup__error')) {
        setTimeout(() => Drawers.open('membership'), 300);
      }

      document.addEventListener('drawer:close', (e) => {
        if (e.detail.name === 'membership') {
          const s = storage.get(this.key, {});
          if (!s.joined) storage.set(this.key, { dismissed: Date.now() });
        }
      });

      $$('[data-signup-form]').forEach((form) => {
        form.addEventListener('submit', () => {
          const btn = $('[data-signup-button]', form);
          const label = $('[data-signup-label]', form);
          if (btn) { btn.classList.add('is-loading'); btn.setAttribute('aria-busy', 'true'); }
          if (label) label.textContent = theme.strings.joining;
        });
      });

      // Auto-open once for new visitors (not again for 30 days after closing, never after joining)
      const thirtyDays = 30 * 24 * 60 * 60 * 1000;
      const recentlyDismissed = state.dismissed && Date.now() - state.dismissed < thirtyDays;
      if (theme.settings.membershipAutoOpen && !state.joined && !recentlyDismissed && !posted && !Shopify_designMode()) {
        setTimeout(() => {
          if (!Drawers.current && !document.hidden) Drawers.open('membership');
        }, theme.settings.membershipDelay || 20000);
      }
    }
  };

  function Shopify_designMode() {
    return !!(window.Shopify && window.Shopify.designMode);
  }

  /* ---------- Facets (filters + sort) ---------- */
  const Facets = {
    init() {
      $$('[data-facets-form]').forEach((form) => {
        form.addEventListener('change', (e) => {
          if (e.target.matches('[data-facets-sort], input[type="checkbox"]')) this.submit(form);
        });
        form.addEventListener('submit', (e) => { e.preventDefault(); this.submit(form); });
      });
    },
    submit(form) {
      const params = new URLSearchParams();
      new FormData(form).forEach((value, key) => { if (value !== '') params.append(key, value); });
      const results = $('[data-collection-results]');
      if (results) results.classList.add('is-loading');
      window.location.href = `${form.getAttribute('action')}?${params.toString()}`;
    }
  };

  /* ---------- Related products ---------- */
  async function initRelated() {
    const el = $('[data-related]');
    if (!el) return;
    try {
      const res = await fetch(el.getAttribute('data-url'));
      if (!res.ok) return;
      const doc = parseHTML(await res.text());
      const fresh = $('[data-related]', doc);
      if (fresh && $('[data-related-grid] li', fresh)) {
        el.innerHTML = fresh.innerHTML;
        Wishlist.sync();
      }
    } catch (e) { /* keep fallback */ }
  }

  /* ---------- Scroll reveals ---------- */
  function initReveal() {
    const targets = $$('[data-reveal]');
    if (!document.body.classList.contains('motion-on') || reduceMotion || !('IntersectionObserver' in window)) {
      targets.forEach((t) => t.classList.add('is-visible'));
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) { entry.target.classList.add('is-visible'); io.unobserve(entry.target); }
      });
    }, { rootMargin: '0px 0px -12% 0px', threshold: 0.05 });
    targets.forEach((t) => io.observe(t));
  }

  /* ---------- Theme editor support ---------- */
  function initDesignMode() {
    document.addEventListener('shopify:section:load', (e) => {
      initProductForms(e.target);
      Wishlist.sync();
      $$('[data-reveal]', e.target).forEach((t) => t.classList.add('is-visible'));
      if (e.target.querySelector('[data-header]')) { Header.init(); Mega.init(); }
    });
    document.addEventListener('shopify:section:select', (e) => {
      $$('[data-reveal]', e.target).forEach((t) => t.classList.add('is-visible'));
    });
  }

  /* ---------- Boot ---------- */
  function boot() {
    Drawers.init();
    Header.init();
    Mega.init();
    Cart.init();
    initProductForms();
    Gallery.init();
    QuickView.init();
    Search.init();
    Wishlist.init();
    Membership.init();
    Facets.init();
    initRelated();
    initReveal();
    initDesignMode();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
